import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { map, switchMap } from 'rxjs/operators';

import { Logger, UrlParamsService } from '@app/core';
import { AppService, DocumentService, DynamicDocumentService, ErrorHandlerService, VerificationService, WhitelabelService } from '@app/services';
import {
  Document,
  DocumentAdditionalConfigTab,
  DocumentInput,
  DynamicDocumentData,
  DynamicTemplateThemeEnum,
  FloatingField,
  FooterEnum,
  User,
  Whitelabel,
  FooterTypeEnum,
  UserBiometricValidation,
  UserBiometricValidationStatusEnum
} from '@app/models';
import { DocumentConfigsFormComponent } from '../shared/document-configs-form/document-configs-form.component';

const log = new Logger('DocumentsEditComponent');

@Component({
  selector: 'app-documents-edit',
  templateUrl: './documents-edit.component.html',
  styleUrls: ['./documents-edit.component.scss'],
  standalone: false
})
export class DocumentsEditComponent implements OnInit, OnDestroy {
  documentId: string;
  currentUser: User;
  document: Document;
  documentInput: DocumentInput;
  floatingFields: FloatingField[];
  isLoading = false;
  validationErrors: { [k: string]: any } = {};
  isDocumentWhitelabelLoaded = false;
  isPdfA = false;

  documentWhitelabel: Whitelabel = {
    main_color: null,
    logo: '',
    pdf_logo: '',
    pdf_logo_mini: ''
  };

  dynamicDocumentViewOptions: { isDynamicDocument: boolean; content: string; theme: DynamicTemplateThemeEnum };

  isMobile: boolean;
  currentAdditionalConfigTab: DocumentAdditionalConfigTab = DocumentAdditionalConfigTab.AdditionalConfigDocument;
  pageTopTabLoad: { [key in DocumentAdditionalConfigTab]?: boolean } = {
    [DocumentAdditionalConfigTab.AdditionalConfigDocument]: true
  };

  readonly DocumentAdditionalConfigTab = DocumentAdditionalConfigTab;
  @ViewChild('configsForm', { static: false }) private configsFormComponent!: DocumentConfigsFormComponent;

  constructor(
    public documentService: DocumentService,
    public appService: AppService,
    public whitelabelService: WhitelabelService,
    public urlParamsService: UrlParamsService,
    private router: Router,
    private route: ActivatedRoute,
    private errorHandlerService: ErrorHandlerService,
    private dynamicDocumentService: DynamicDocumentService,
    private verificationService: VerificationService
  ) {
    this.documentId = this.route.snapshot.params.id;
  }

  ngOnInit() {
    this.isMobile = window.innerWidth <= 992;
    this.documentService
      .documentWithFloatingFields({ id: this.documentId })
      .pipe(
        switchMap(({ document, floatingFields }) =>
          this.documentService.documentWatchers({ id: this.documentId }).pipe(map(watchers => ({ document, floatingFields, watchers: watchers.map(w => w.email) })))
        )
      )
      .subscribe({
        next: ({ document, floatingFields, watchers }) => {
          this.document = document;
          this.isPdfA = !!this.document.configs.pdfa;
          this.floatingFields = floatingFields;
          this.documentInput = {
            name: this.document.name,
            ignore_cpf: this.document.ignore_cpf,
            ignore_birthdate: this.document.ignore_birthdate || false,
            refusable: this.document.refusable,
            sortable: this.document.sortable,
            stop_on_rejected: this.document.stop_on_rejected,
            deadline_at: this.document.deadline_at,
            cc: (this.document.cc || []).map(cc => ({ email: cc })) || null,
            reply_to: this.document.reply_to,
            reminder: this.document.reminder,
            expiration: { notify_at: this.document.expiration_at || null, days_before: this.document.notify_in || null },
            message: this.document.message,
            footer: this.document.footer as FooterEnum,
            footer_type: this.document.footer_type || FooterTypeEnum.Standard,
            qualified: this.document.qualified, // adiciona qualified para verificação das opções de edição
            new_signature_style: this.document.new_signature_style,
            show_audit_page: this.document.show_audit_page,
            scrolling_required: this.document.scrolling_required,
            configs: {
              notification_signed: this.document.configs.notification_signed,
              notification_finished: this.document.configs.notification_finished,
              notify_watchers: this.document.configs.notify_watchers,
              lock_user_data: this.document.configs.lock_user_data,
              signature_appearance: this.document.configs.signature_appearance
            },
            locale: {
              timezone: this.document.locale.timezone,
              date_format: this.document.locale.date_format,
              language: this.document.locale.language,
              country: this.document.locale.country
            },
            email_template_id: this.document.email_template_id || null,
            whatsapp_template: this.document.whatsapp_template,
            watchers: watchers || []
          };

          if (document.is_dynamic_document) {
            this.setDynamicDocumentViewOptions(document.files.original);
          }
        }
      });

    const whitelabel$ = this.appService.isAutentiqueDomain()
      ? this.whitelabelService.loadWhitelabelByDocument({ documentId: this.documentId })
      : this.whitelabelService.loadWhitelabelByDomain({ fetchPolicy: 'cache-first' });
    whitelabel$.subscribe({
      next: whitelabel => {
        this.isDocumentWhitelabelLoaded = true;
        this.documentWhitelabel = this.whitelabelService.buildWhitelabel(whitelabel);
      }
    });
  }

  ngOnDestroy() {}

  updateDocument() {
    this.isLoading = true;
    const documentInput = this.configsFormComponent.parsedDocumentInput();

    delete documentInput.qualified; // remove o qualified adicionado na verificação acima
    delete documentInput.reply_to; // reply-to não pode ser alterado
    delete documentInput.locale?.language; // language não pode ser alterado
    delete documentInput.locale?.country; // country não pode ser alterado

    this.documentService
      .update({ id: this.document.id, document: documentInput })
      .pipe(switchMap(() => this.documentService.clearPdfCache({ id: this.document.id })))
      .subscribe({
        next: () => this.router.navigate(['/documentos/', this.document.id]),
        error: error => {
          this.isLoading = false;
          const handledError = this.errorHandlerService.handle(error, { ignoreGraphqlErrorNotifications: true, validationMode: true, ignoreNotificationOn: ['user_biometric_verification_required'] });
          this.validationErrors = handledError.validation || {};
          if (handledError.message === 'user_biometric_verification_required') {
            this.verificationService.validateUserCreateDocument().subscribe({
              next: (data: UserBiometricValidation) => {
                if (data?.status === UserBiometricValidationStatusEnum.Completed) {
                  this.updateDocument();
                }
              }
            });
          }
        }
      });
  }

  changeDocumentConfigTab(tab: DocumentAdditionalConfigTab): void {
    if (this.currentAdditionalConfigTab === tab) return;

    this.currentAdditionalConfigTab = tab;
    if (!this.pageTopTabLoad[tab]) {
      this.pageTopTabLoad[tab] = true;
    }
  }

  setDynamicDocumentViewOptions(originalFile: string) {
    this.dynamicDocumentService.getDynamicDocumentData(originalFile).subscribe((data: DynamicDocumentData) => {
      this.dynamicDocumentViewOptions = {
        isDynamicDocument: true,
        content: this.appService.cleanAttqDocumentMargins(data.content),
        theme: data.theme
      };
    });
  }
}
